import type { Texture } from 'three'
import { LinearFilter, TextureLoader } from 'three'
import type { SiteCamera } from '@/entities/siteCamera'
import type { Site } from '@/entities/site'
import { getDeviceWebGLMaxTextureLimit, isLowEndDevice } from '@/lib/utils'
import { socketUnitsToFov } from '@/lib/site-viewer/fov-conversion'

const textureLoader: TextureLoader = new TextureLoader()
const MAX_TEXTURE_LIMIT = 6000
const MOBILE_MAX_TEXTURE_LIMIT = 2048

type SiteData = {
  site: Site
  texture: Texture
}

const getSite = async (siteId: number, { uuid }: { uuid?: string }): Promise<SiteData> => {
  const params: any = { include: ['hotspots'] }
  try {
  } catch (e) {}
  if (isLowEndDevice() || getDeviceWebGLMaxTextureLimit() <= MAX_TEXTURE_LIMIT) {
    params.width = MOBILE_MAX_TEXTURE_LIMIT
  }

  const site = await $fetch<Site>(`v2/sites/${siteId}`, {
    ...useFetchSharedOptions(),
    params,
    headers: {
      ...useFetchSharedOptions().headers,
      'X-Deck-Auth-UUID': uuid ?? 'null',
    },
  })

  const texture = await loadTexture(site.media_url)

  // Explanation: https://threejs.org/manual/#en/textures#filtering-and-mips
  texture.minFilter = LinearFilter

  return {
    site,
    texture,
  }
}

const getSiteCamera = async (siteId: number, cameraId: number | null, { uuid }: { uuid?: string }): Promise<SiteCamera | null> => {
  try {
    if (!cameraId) {
      return null
    }

    const siteCameraResponse = await $fetch<SiteCamera>(`v2/sites/${siteId}/cameras/${cameraId}`, {
      ...useFetchSharedOptions(),
      headers: {
        ...useFetchSharedOptions().headers,
        'X-Deck-Auth-UUID': uuid ?? 'null',
      },
    })

    if (siteCameraResponse.zoom) {
      // 90 units (maximum) from server should map to 120 degrees FOV here.
      siteCameraResponse.zoom = socketUnitsToFov(siteCameraResponse.zoom)
    }

    return siteCameraResponse
  } catch (error) {
    return null
  }
}

const loadTexture = async (url: string): Promise<Texture> => {
  return new Promise((resolve, reject) => {
    textureLoader.load(
      url,
      texture => {
        resolve(texture)
      },
      undefined,
      (error: unknown) => {
        reject(error)
      }
    )
  })
}

export { getSite, getSiteCamera }
